import { trpcTransformer } from '@sweetistics/trpc-shared';
import type { CreateTRPCClientOptions, TRPCLink } from '@trpc/client';
import { createTRPCClient, httpBatchStreamLink } from '@trpc/client';
import { httpLink } from '@trpc/client/links/httpLink';
import { loggerLink } from '@trpc/client/links/loggerLink';
import { env } from '@/env';
import { fetchWithTimeout } from '@/lib/network/fetch-with-timeout';
import { createQueryClient } from '@/lib/query-client';
import { isDevelopmentRuntime, isTestRuntime } from '@/lib/runtime-flags';
import { isAutomationRuntime } from '@/lib/runtime-mode';
import { isTruthyEnvFlag } from '@/lib/utils/env-flags';
import { getRecordValue, isRecord } from '@/lib/utils/type-guards';
import type { AppRouter } from '@/types/app-router';

const TRPC_ENDPOINT = '/api/trpc';

const isPlaywrightAutomationRuntime = isAutomationRuntime();

export type TrpcClientMode = 'prod' | 'test';

const getBaseUrl = () => {
  if (globalThis.window !== undefined) {
    return '';
  }

  const environmentUrl = env.NEXT_PUBLIC_APP_URL?.trim();
  if (environmentUrl && environmentUrl.length > 0) {
    return environmentUrl.endsWith('/') ? environmentUrl.slice(0, -1) : environmentUrl;
  }

  return 'http://localhost:3000';
};

/**
 * Dev-only serializer used by the tRPC logger. React Query collapses complex
 * values to strings like "[object Response]" which isn't actionable. This
 * walks the payload, extracting request/response metadata, while guarding
 * against circular structures and runaway nesting so our console output stays
 * readable.
 */
type TrpcLoggerPayload = Record<string, unknown> & {
  result?: unknown;
};

const isHeaderTuple = (value: unknown): value is [string, string] =>
  Array.isArray(value) && value.length === 2 && value.every((entry) => typeof entry === 'string');

const isHeaderTupleArray = (value: unknown): value is [string, string][] =>
  Array.isArray(value) && value.every((tuple) => isHeaderTuple(tuple));

const isBodyInit = (value: unknown): value is BodyInit | null => {
  if (value === null || typeof value === 'string') {
    return true;
  }
  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    return true;
  }
  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    return true;
  }
  if (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) {
    return true;
  }
  if (typeof ReadableStream !== 'undefined' && value instanceof ReadableStream) {
    return true;
  }
  if (value instanceof ArrayBuffer) {
    return true;
  }
  return ArrayBuffer.isView(value);
};

const formatForLog = (value: unknown, seen = new WeakSet<object>(), depth = 0): unknown => {
  if (
    value === undefined ||
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (typeof value === 'function') {
    return `[Function${value.name ? `:${value.name}` : ''}]`;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }

  if (globalThis.Response && value instanceof Response) {
    return {
      ok: value.ok,
      status: value.status,
      statusText: value.statusText,
      url: value.url,
      redirected: value.redirected,
      type: value.type,
      headers: (() => {
        try {
          return Object.fromEntries(value.headers.entries());
        } catch {
          return;
        }
      })(),
    };
  }

  if (globalThis.Headers && value instanceof Headers) {
    try {
      return Object.fromEntries(value.entries());
    } catch {
      return '[Headers]';
    }
  }

  if (globalThis.URLSearchParams && value instanceof URLSearchParams) {
    return Object.fromEntries(value.entries());
  }

  if (Array.isArray(value)) {
    if (depth > 4) {
      return '[Truncated Array]';
    }
    return value.map((entry) => formatForLog(entry, seen, depth + 1));
  }

  if (!isRecord(value)) {
    return value;
  }

  const objectValue = value;
  if (seen.has(objectValue)) {
    return '[Circular]';
  }
  seen.add(objectValue);

  if (depth > 4) {
    return '[Truncated Object]';
  }

  return Object.fromEntries(
    Object.entries(objectValue).map(([key, entry]) => [key, formatForLog(entry, seen, depth + 1)])
  );
};

type TrpcRequestInitEsque = {
  body?: FormData | string | null | Uint8Array<ArrayBuffer> | Blob | File;
  headers?: [string, string][] | Record<string, string>;
  method?: string;
  signal?: AbortSignal | null | undefined;
};

type TrpcFetch = (input: RequestInfo | URL | string, init?: RequestInit | TrpcRequestInitEsque) => Promise<Response>;

const trpcFetch: TrpcFetch = (input, init) => {
  const isRequestInstance = globalThis.Request && input instanceof Request;
  const isUrlInstance = globalThis.URL && input instanceof URL;

  let normalizedInput: RequestInfo;
  if (isRequestInstance) {
    normalizedInput = input;
  } else if (typeof input === 'string') {
    normalizedInput = input;
  } else if (isUrlInstance) {
    normalizedInput = input.toString();
  } else {
    normalizedInput = isRecord(input) && typeof input.url === 'string' ? input.url : '[Unsupported request input]';
  }

  const initRecord = isRecord(init) ? init : undefined;
  const normalizedInit: RequestInit = {
    credentials: 'same-origin',
    signal: null,
  };

  if (initRecord) {
    const method = getRecordValue(initRecord, 'method');
    if (typeof method === 'string' && method.length > 0) {
      normalizedInit.method = method;
    }

    const headers = getRecordValue(initRecord, 'headers');
    if (isHeaderTupleArray(headers)) {
      normalizedInit.headers = headers;
    } else if (isRecord(headers)) {
      const headerEntries = Object.entries(headers).filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string'
      );
      if (headerEntries.length > 0) {
        normalizedInit.headers = Object.fromEntries(headerEntries);
      }
    }

    const body = getRecordValue(initRecord, 'body');
    if (body !== undefined && isBodyInit(body)) {
      normalizedInit.body = body;
    }

    const credentials = getRecordValue(initRecord, 'credentials');
    if (credentials === 'include' || credentials === 'omit' || credentials === 'same-origin') {
      normalizedInit.credentials = credentials;
    }

    const rawSignal = getRecordValue(initRecord, 'signal');
    const signalValue =
      rawSignal && typeof AbortSignal !== 'undefined' && rawSignal instanceof AbortSignal ? rawSignal : null;
    normalizedInit.signal = signalValue;
  }

  return fetchWithTimeout(normalizedInput, normalizedInit);
};

const resolveTrpcClientMode = (mode?: TrpcClientMode): TrpcClientMode => {
  if (mode !== undefined) {
    return mode;
  }

  if (isTestRuntime) {
    return 'test';
  }

  return isPlaywrightAutomationRuntime ? 'test' : 'prod';
};

const createLinks = (mode: TrpcClientMode): TRPCLink<AppRouter>[] => {
  const url = `${getBaseUrl()}${TRPC_ENDPOINT}`;
  const isServer = globalThis.window === undefined;
  const isDevelopment = isDevelopmentRuntime; // Mirrors server batching toggle—keep transports aligned.
  const developmentBatchingEnabled = isDevelopment && isTruthyEnvFlag(env.NEXT_PUBLIC_TRPC_BATCH_DEV);
  const useBatching = mode === 'prod' && (!isDevelopment || developmentBatchingEnabled);
  let trpcSource: 'test' | 'rsc' | 'browser';
  if (mode === 'test') {
    trpcSource = 'test';
  } else {
    trpcSource = isServer ? 'rsc' : 'browser';
  }
  const headers = () => ({
    'x-trpc-source': trpcSource,
  });

  const links: TRPCLink<AppRouter>[] = [];

  const shouldLogTrpcTraffic = mode === 'prod' && isDevelopment;
  if (shouldLogTrpcTraffic) {
    links.push(
      loggerLink({
        enabled: (options) => {
          if (globalThis.window === undefined) {
            return options.direction === 'down' && options.result instanceof Error;
          }
          return true;
        },
        logger: (event) => {
          const { direction, type, path } = event;
          const prefix = `[trpc] ${direction === 'down' ? '<<' : '>>'} ${type} ${path}`;
          const payload: TrpcLoggerPayload = {
            ...event,
            context: formatForLog('context' in event ? event.context : undefined),
            input: formatForLog('input' in event ? event.input : undefined),
          };

          if ('result' in event) {
            const eventWithResult = event as { result?: unknown };
            payload.result = formatForLog(eventWithResult.result);
          }
          try {
            console.info(prefix, payload);
          } catch {
            console.info(prefix);
          }
        },
      })
    );
  }

  // Dev defaults to single requests; prod always batches (dev can opt in via NEXT_PUBLIC_TRPC_BATCH_DEV).
  links.push(
    useBatching
      ? httpBatchStreamLink({
          url,
          maxURLLength: 2048,
          headers,
          transformer: trpcTransformer,
          fetch: trpcFetch,
        })
      : httpLink({
          url,
          headers,
          transformer: trpcTransformer,
          fetch: trpcFetch,
        })
  );

  return links;
};

type TrpcClientOptions = CreateTRPCClientOptions<AppRouter>;

export type TrpcClient = ReturnType<typeof createTRPCClient<AppRouter>>;

type TrpcClientFactoryOptions = Partial<TrpcClientOptions> & {
  mode?: TrpcClientMode;
};

export const createTrpcClient = (options: TrpcClientFactoryOptions = {}): TrpcClient => {
  const { mode, ...clientOptions } = options;
  const resolvedMode = resolveTrpcClientMode(mode);
  const baseLinks = createLinks(resolvedMode);

  return createTRPCClient<AppRouter>({
    ...clientOptions,
    links: clientOptions.links ?? baseLinks,
  });
};

export const createTrpcQueryClient = () => createQueryClient();

export type { TrpcClientOptions };
